import {Fade, Zoom} from "@material-ui/core";
import AppImage from "src/js/components/commons/AppImage";
import Paper from "@material-ui/core/Paper";
import TextView from "src/js/components/commons/TextView";
import {FontWeightMedium} from "src/res/FontWeight";
import React, {useLayoutEffect, useRef, useState} from "react";
import {baseStyles, withMultipleStyles} from "src/styles/Styles";
import {colorWithAlpha, getWords, isRTLFirstChar} from "src/js/utils/AppUtils";
import FocusHelperView from "src/js/components/commons/FocusHelperView";
import TouchableOpacity from "src/js/components/commons/TouchableOpacity";
import {ACTIVITY_SLIDESHOW_DURATION_MAX, ACTIVITY_SLIDESHOW_DURATION_MIN} from "src/js/constants/ApiConstants";
import ApiHelper from "src/js/utils/ApiHelper";

const useStyles = () => ({
  funCardItemImage: {
    aspectRatio: 4/3,
    borderRadius: 16,
    boxShadow: '0 8px 12px 0 rgba(0, 0, 0, 0.2), 0 8px 20px 0 rgba(0, 0, 0, 0.2)',
  },
})

const TextReveal = (props) => {
  const {classes, text, uiConfigs, delay, size, isLTR, onRevealed} = props;

  const words = useRef();
  const isTextRTL = useRef();
  const [counter, setCounter] = useState(-1);

  useLayoutEffect(() => {
    setCounter(-1);
    words.current = getWords(text);
    if (words.current && words.current.length > 0) {
      isTextRTL.current = isRTLFirstChar(words.current[0]);
    }

    let intervalRef = null;
    let timeoutRef = setTimeout(() => {
      let wordIndex = 0;
      intervalRef = setInterval(() => {
        if (wordIndex < words.current.length) {
          setCounter(wordIndex);
          wordIndex++;
        } else {
          if (onRevealed) {
            onRevealed();
          }
          clearInterval(intervalRef);
        }
      }, 200);
    }, delay);

    return () => {
      setCounter(-1);
      if (intervalRef) {
        clearInterval(intervalRef);
      }
      if (timeoutRef) {
        clearTimeout(timeoutRef);
      }
      words.current = null;
      isTextRTL.current = null;
    }
  }, [text, onRevealed])

  const factor = size/720;
  const textPadding = 16 * factor;
  const textMargin= 12 * factor;
  const fontSize= 50 * factor;
  return (
    <div className={classes.viewRow} style={{flexDirection: (isTextRTL.current && isLTR) || (!isTextRTL.current && !isLTR) ? 'row-reverse' : 'row', flexWrap: 'wrap', justifyContent: 'center', margin: 16}}>
      {words.current && words.current.map((word, index) => (
        <Zoom key={index} in={index <= counter}>
          <Paper elevation={0} style={{borderRadius: fontSize/2, backgroundColor: colorWithAlpha(uiConfigs.themeColor, 0.9), padding: textPadding, paddingTop: textPadding*.6, paddingBottom: textPadding*.6, margin: textMargin}}>
            <TextView fontSizeN={fontSize} variant={'h6'} text={word} color={uiConfigs.titleColor} style={{fontWeight: FontWeightMedium}}/>
          </Paper>
        </Zoom>
      ))}
    </div>
  )
}

class ActivitySlides extends React.PureComponent {

  constructor(props) {
    super(props);

    const {durations, totalDuration} = this.getDurationInfo(this.props.items);
    this.state = {
      play: false,
      currentIndex: 0,
      items: this.props.items,
      timePassedMs: 0,
      durations: durations,
      totalDuration: totalDuration,
    }
  }

  componentWillUnmount() {
    if (this.refTimeout) {
      clearTimeout(this.refTimeout);
      this.refTimeout = null;
    }
    if (this.refInterval) {
      clearInterval(this.refInterval);
      this.refInterval = null;
    }
  }

  onDidFocus = () => {
    if (this.wasTimeoutRunning) {
      this.wasTimeoutRunning = false;
      this.moveToNextIndex();
    }
  }

  onDidBlur = () => {
    if (this.refTimeout) {
      clearTimeout(this.refTimeout);
      this.refTimeout = null;
      this.wasTimeoutRunning = true;
    }
    if (this.refInterval) {
      clearInterval(this.refInterval);
      this.refInterval = null;
    }
  }

  simulatePlay = () => {
    this.togglePlay();
  }

  resetForPreview(items, index = null) {
    let {currentIndex} = this.state;
    if (index || index === 0) {
      currentIndex = index;
    }

    if (this.refTimeout) {
      clearTimeout(this.refTimeout);
      this.refTimeout = null;
    }
    if (this.refInterval) {
      clearTimeout(this.refInterval);
      this.refInterval = null;
    }
    const {durations, totalDuration} = this.getDurationInfo(items);
    this.setState({items: items, currentIndex: currentIndex, blur: false, durations: durations, totalDuration: totalDuration}, () => {
      if (this.state.play) {
        this.moveToNextIndex();
      }
    });
  }

  reset(items) {
    if (this.refTimeout) {
      clearTimeout(this.refTimeout);
      this.refTimeout = null;
    }
    if (this.refInterval) {
      clearTimeout(this.refInterval);
      this.refInterval = null;
    }
    const {durations, totalDuration} = this.getDurationInfo(items);
    this.setState({items: items, currentIndex: 0, play: false, ended: false, durations: durations, totalDuration: totalDuration});
  }

  getDurationInfo(items) {
    let durations = null;
    let totalDuration = 0;
    if (items && items.length > 0) {
      durations = [];
      let index = 0;
      items.forEach((item) => {
        let duration = item.duration;
        if (duration < ACTIVITY_SLIDESHOW_DURATION_MIN || duration > ACTIVITY_SLIDESHOW_DURATION_MAX) {
          duration = Math.ceil(((ACTIVITY_SLIDESHOW_DURATION_MIN + ACTIVITY_SLIDESHOW_DURATION_MAX) / 2));
        }
        const words = getWords(item.title);
        if (words) {
          duration = Math.max(duration, (words.length + 2) * .2);
        }
        durations.push(duration);
        totalDuration += duration;
        index++;
      });
    }
    return {
      durations,
      totalDuration
    }
  }

  render() {
    const {classes, uiConfigs, width, isLTR, simulation, canAction} = this.props;
    const {currentIndex, durations, totalDuration, lastTime, play, ended} = this.state;
    const styleBlur = !play ? {filter: 'blur(4px)', opacity: 0.8} : null;
    return (
      <div className={classes.absoluteFillCenter} style={{backgroundColor: colorWithAlpha(uiConfigs.themeColor, 0.6)}}>
        <FocusHelperView
          onAppDidFocus={this.onDidFocus}
          onAppDidBlur={this.onDidBlur}
        />

        <TouchableOpacity onClick={this.togglePlay} className={classes.absoluteFillCenter}>
          {this.renderSlide()}
        </TouchableOpacity>

        {!play && !ended && !simulation && canAction ?
          <div style={{position: "absolute", bottom: 20, top: 20, left: 20, right: 20, display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', pointerEvents: 'none'}}>
            <Paper elevation={0} style={{backgroundColor: colorWithAlpha(uiConfigs.titleColor, 0.9), padding: 16, paddingTop: 6, paddingBottom: 6}}>
              <TextView fontSizeN={uiConfigs.fontSize} variant={'h4'} textId={'activity.clickToPlay'} color={uiConfigs.themeColor} style={{fontWeight: FontWeightMedium}}/>
            </Paper>
          </div>
          : null
        }

        {!simulation ?
          <div style={{position: "absolute", bottom: 0, left: 0, right: 0, display: 'flex', flexDirection: 'row', ...styleBlur}}>
            {durations && durations.map((duration, index) => {
              let sWidth = index < currentIndex ? '100%' : 0;
              if (lastTime && index === currentIndex) {
                sWidth = (((Date.now() - lastTime)/1000)/duration)*100 + '%';
              }
              return (
                <div key={index} className={classes.viewRow} style={{height: 6, marginLeft: isLTR && index > 0 ? 4 : 0, marginRight: !isLTR && index > 0 ? 4 : 0, width: Math.floor(duration/totalDuration * width), backgroundColor: colorWithAlpha(uiConfigs.themeColor, 0.3)}}>
                  <div className={classes.viewRow} style={{width: sWidth, height: 6, backgroundColor: colorWithAlpha(uiConfigs.themeColor, 0.5)}}/>
                </div>
              );
            })}
          </div>
          : null
        }
      </div>
    );
  }

  renderSlide() {
    const {classes, isXS, uiConfigs, showcase, width, height, isLTR} = this.props;
    const {items, currentIndex, play} = this.state;
    if (currentIndex >= items.length) {
      return null;
    }

    const item = items[currentIndex];
    let isHorizontal = width > height * 1.4;
    let mediaWidth = width;
    let mediaHeight = height;
    let textWidth = width;
    let textHeight = height;
    let imageInfo = null;
    let imageStyle= {width: mediaWidth, height: mediaHeight};
    if (item.imageUrl && item.title) {
      let imageAspectRatio = mediaWidth/mediaHeight;
      if (item.imageUrl) {
        imageInfo = ApiHelper.getInfoForImageUrl(item.imageUrl);
        if (imageInfo && imageInfo.width > 0 && imageInfo.height > 0) {
          imageAspectRatio = imageInfo.width/imageInfo.height;
        }
      }
      if (isHorizontal) {
        mediaWidth = Math.min(mediaHeight, mediaHeight*imageAspectRatio, mediaWidth*0.6);
        textWidth = width - mediaWidth;
        imageStyle = imageInfo ? {width: mediaWidth, height: mediaHeight} : {maxWidth: mediaWidth, height: mediaHeight};
      } else {
        mediaHeight = Math.min(mediaWidth, mediaWidth/imageAspectRatio, mediaHeight*0.6);
        textHeight = height - mediaHeight;
        imageStyle = imageInfo ? {width: mediaWidth, height: mediaHeight} : {width: mediaWidth, maxHeight: mediaHeight};
      }
    }

    const styleBlur = !play ? {filter: 'blur(4px)', opacity: 0.8} : null;
    return (
      <div className={classes.absoluteFillCenter} style={styleBlur}>
        <Fade in={true}>
          <div className={isHorizontal ? classes.flexRow : classes.flexColumn}>
            {item.imageUrl ?
              <AppImage
                style={imageStyle}
                resizeMode={'contain'}
                src={item.imageUrl}
              />
              : null
            }
            {play && item.title ?
              <div className={classes.flexColumn} style={{justifyContent: 'center'}}>
                <div className={classes.flexColumn} style={{width: textWidth, height: textHeight, justifyContent: 'center'}}>
                  <TextReveal
                    key={currentIndex}
                    classes={classes}
                    isXS={isXS}
                    isLTR={isLTR}
                    showcase={showcase}
                    uiConfigs={uiConfigs}
                    text={item.title}
                    size={Math.min(textWidth, textHeight)}
                    delay={1}
                  />
                </div>
              </div>
              : null
            }
          </div>
        </Fade>
      </div>
    );
  }

  togglePlay = () => {
    if (this.state.ended) {
      return;
    }

    this.setState({play: !this.state.play}, () => {
      if (this.state.play) {
        this.moveToNextIndex();
      } else {
        if (this.refTimeout) {
          clearTimeout(this.refTimeout);
          this.refTimeout = null;
        }
        if (this.refInterval) {
          clearTimeout(this.refInterval);
          this.refInterval = null;
        }
      }
    });
  }

  moveToNextIndex = () => {
    if (this.refTimeout) {
      clearTimeout(this.refTimeout);
      this.refTimeout = null;
    }
    if (this.refInterval) {
      clearInterval(this.refInterval);
      this.refInterval = null;
    }

    const {items, currentIndex, durations} = this.state;
    if (!items || items.length === 0) {
      return;
    }
    const item = items[currentIndex];
    const title = item.title && item.title.trim().length > 0 ? item.title : null;
    if (currentIndex < items.length) {
      if (!item.imageUrl && !title) {
        return;
      }
    }

    const duration = durations[currentIndex];
    this.refTimeout = setTimeout(this.handleTimeout, duration * 1000);
    this.setState({lastTime: Date.now()})
    this.refInterval = setInterval(this.handleTimerInterval, 100);
  }

  handleTimeout = () => {
    const {simulation, onDone} = this.props;
    let {items, currentIndex} = this.state;
    currentIndex++;
    if (currentIndex === items.length) {
      if (this.refInterval) {
        clearInterval(this.refInterval);
        this.refInterval = null;
      }
      this.setState({play: false, ended: true});
      setTimeout(() => {
        if (onDone) {
          onDone();
        }
      }, simulation ? 0 : 1000);
    } else {
      this.setState({currentIndex: currentIndex}, this.moveToNextIndex);
    }
  }

  handleTimerInterval = () => {
    this.setState({reload: !this.state.reload});
  }

}

export default withMultipleStyles(baseStyles, useStyles)(ActivitySlides);
